import { useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X, Video, LayoutGrid, Square } from 'lucide-react';
import { Button } from '../ui/Button';
import { Select } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { LiveVideoPlayer } from './LiveVideoPlayer';
import { HistoricalVideoPlayer } from './HistoricalVideoPlayer';
import { useAuth } from '../../context/AuthContext';
import { useVehicleCameras } from '../../hooks/useVehicleCameras';
import { CAMERA_LABEL_LABELS } from '../../utils/constants';
import type { VehicleCamera } from '../../types/vehicle';

interface VehicleCameraViewerProps {
  vehicleId: string;
  /** Para el título del overlay flotante — opcional, solo se usa si `floating`. */
  plate?: string;
  /**
   * Monitoreo (VehicleDetailPanel) quiere el reproductor grande, flotando
   * sobre la pantalla — el look original de esa pantalla. Vehículos y
   * Conductores lo quieren embebido en el flujo del modal. Mismo mecanismo
   * por debajo (`LiveVideoPlayer`), solo cambia el empaque visual.
   */
  floating?: boolean;
}

type ViewMode = 'single' | 'grid';
type ContentMode = 'live' | 'history';

// FE-14 — pieza compartida entre VehicleDetailPanel (Monitoreo),
// VehicleDetailModal (Vehículos) y DriverProfileModal (Conductores): trae
// las cámaras reales del vehículo (`useVehicleCameras`) y ofrece dos formas
// de verlas — una a la vez (selector) o todas a la vez (grilla). El backend
// no opina sobre la presentación (ADR-018: "cómo se presenten los canales es
// decisión del frontend") — ambos modos usan el mismo `LiveVideoPlayer`; cada instancia
// HLS diferida se destruye al cerrarse para no conservar una URL efímera.
export function VehicleCameraViewer({ vehicleId, plate, floating = false }: VehicleCameraViewerProps) {
  const { cameras, isLoading } = useVehicleCameras(vehicleId);
  const { user } = useAuth();
  const [mode, setMode] = useState<ViewMode>('single');
  const [contentMode, setContentMode] = useState<ContentMode>('live');
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [isViewing, setIsViewing] = useState(false);

  const selectedCamera: VehicleCamera | undefined =
    cameras.find(c => c.id === selectedCameraId) ?? cameras[0];
  const enabledCameras = cameras.filter(c => c.enabled);

  const switchLiveMode = (next: ViewMode) => {
    setMode(next);
    setIsViewing(false); // cambiar de modo cierra lo que estuviera abierto — evita abrir N streams sin querer
  };
  const switchContentMode = (next: ContentMode) => {
    setContentMode(next);
    setIsViewing(false); // pestaña/cámara/rango siempre desmonta el player anterior
  };
  const mayViewHistory = user?.role !== 'operator';

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
        <Spinner size="sm" />
      </div>
    );
  }

  if (cameras.length === 0) {
    return (
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
        Sin cámaras declaradas para este vehículo.
      </p>
    );
  }

  const playerArea = (
    <>
      {contentMode === 'history' && isViewing && (
        <HistoricalVideoPlayer
          vehicleId={vehicleId}
          cameras={cameras}
          initialCameraId={selectedCamera?.id}
          onClose={() => setIsViewing(false)}
        />
      )}
      {contentMode === 'live' && mode === 'single' && isViewing && selectedCamera && (
        <LiveVideoPlayer vehicleId={vehicleId} camera={selectedCamera} onClose={() => setIsViewing(false)} />
      )}
      {contentMode === 'live' && mode === 'grid' && isViewing && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.625rem' }}>
          {enabledCameras.map(camera => (
            <div key={camera.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: floating ? 'rgba(255,255,255,0.75)' : 'var(--text-secondary)' }}>
                {camera.name || CAMERA_LABEL_LABELS[camera.label]}
              </span>
              <LiveVideoPlayer vehicleId={vehicleId} camera={camera} onClose={() => setIsViewing(false)} />
            </div>
          ))}
        </div>
      )}
    </>
  );

  const activeCameraLabel = contentMode === 'history'
    ? 'HISTÓRICO'
    : mode === 'single' && selectedCamera
      ? selectedCamera.name || CAMERA_LABEL_LABELS[selectedCamera.label]
      : `${enabledCameras.length} cámaras`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant={contentMode === 'live' ? 'primary' : 'ghost'} size="sm" leftIcon={<Video size={13} />}
          onClick={() => switchContentMode('live')}
        >En vivo (diferido)</Button>
        {mayViewHistory && (
          <Button
            variant={contentMode === 'history' ? 'primary' : 'ghost'} size="sm" leftIcon={<Video size={13} />}
            onClick={() => switchContentMode('history')}
          >Histórico</Button>
        )}
        {contentMode === 'live' && cameras.length > 1 && (
          // La grilla queda oculta en móvil (ver .camera-viewer__mode-toggle
          // en index.css): varios reproductores HLS a la vez chocan
          // con el overlay flotante y el bottom-sheet de detalle en pantallas
          // angostas. En mobile, `mode` nunca deja de ser 'single'.
          <div className="camera-viewer__mode-toggle" style={{ gap: '0.25rem' }}>
            <Button
              variant={mode === 'single' ? 'primary' : 'ghost'}
              size="sm"
              leftIcon={<Square size={13} />}
              onClick={() => switchLiveMode('single')}
            >
              Una cámara
            </Button>
            <Button
              variant={mode === 'grid' ? 'primary' : 'ghost'}
              size="sm"
              leftIcon={<LayoutGrid size={13} />}
              onClick={() => switchLiveMode('grid')}
            >
              Grilla ({enabledCameras.length})
            </Button>
          </div>
        )}

        {contentMode === 'live' && mode === 'single' && (
          <>
            {cameras.length > 1 && (
              <Select
                aria-label="Cámara"
                value={selectedCamera?.id ?? ''}
                onChange={e => setSelectedCameraId(e.target.value)}
                options={cameras.map(c => ({ value: c.id, label: c.name || CAMERA_LABEL_LABELS[c.label] }))}
                style={{ flex: '0 0 auto' }}
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Video size={14} />}
              onClick={() => setIsViewing(v => !v)}
              style={{ borderColor: 'var(--border-strong)' }}
            >
              {isViewing ? 'Ocultar transmisión' : '📹 Ver Video en Vivo'}
            </Button>
          </>
        )}

        {contentMode === 'live' && mode === 'grid' && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Video size={14} />}
            onClick={() => setIsViewing(v => !v)}
            style={{ borderColor: 'var(--border-strong)' }}
          >
            {isViewing ? 'Ocultar todas' : `Ver las ${enabledCameras.length} cámaras en vivo`}
          </Button>
        )}
        {contentMode === 'history' && (
          <Button variant="ghost" size="sm" leftIcon={<Video size={14} />} onClick={() => setIsViewing(v => !v)} style={{ borderColor: 'var(--border-strong)' }}>
            {isViewing ? 'Ocultar histórico' : 'Ver histórico'}
          </Button>
        )}
      </div>

      {!floating && playerArea}

      {/* Overlay flotante grande — el look original de Monitoreo. En móvil
          ocupa la pantalla entera y su cuerpo hace scroll.

          Va por PORTAL a document.body a propósito: este componente vive dentro
          del bottom-sheet de Monitoreo, cuyos ancestros tienen `backdrop-filter`
          (y durante el arrastre, `transform`). Cualquiera de esos crea un bloque
          contenedor para `position: fixed`, así que el overlay dejaba de anclarse
          al viewport y quedaba atrapado dentro del sheet —se veía como un
          recuadro vacío sobre el panel en vez de a pantalla completa. Al portarlo
          al body, el `fixed` sí se mide contra el viewport. */}
      {floating && isViewing && createPortal(
        <div className={clsx(
          'camera-viewer__overlay',
          (contentMode === 'history' || (mode === 'grid' && enabledCameras.length > 1)) && 'camera-viewer__overlay--wide',
        )}>
          <div className="camera-viewer__overlay-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', color: '#FFF', fontSize: '13.5px', fontWeight: 800 }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 10px #10B981' }} />
              <span>{contentMode === 'history' ? 'VIDEO HISTÓRICO' : 'VIDEO EN VIVO DIFERIDO'}{plate ? ` — ${plate}` : ''} · {activeCameraLabel}</span>
            </div>
            <button
              onClick={() => setIsViewing(false)}
              style={{ background: 'none', border: 'none', color: '#FFF', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Cerrar reproductor"
            >
              <X size={18} />
            </button>
          </div>

          <div className={clsx(
            'camera-viewer__overlay-body',
            (contentMode === 'history' || mode === 'grid') && 'camera-viewer__overlay-body--padded',
          )}>
            {playerArea}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
